import { AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, Input, OnInit, Output, ViewChild, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EmployeeService } from '@app/core/services/employee.service';
import { StylingColoursService } from '@app/modules/site-settings-styling/components/styling-colours/services/styling-colours.service';
import { ChatMessage, ChatSessionService } from './services/chat-session.service';

export const CHAT_BOT_ROUTE = '/Assistant';
const DEFAULT_ACCENT = '#ff9e18';

interface Suggestion { key: string; text: string; icon: string; }

@Component({
  selector: 'app-chat-bot',
  templateUrl: './chat-bot.component.html',
  styleUrls: ['./chat-bot.component.scss']
})
export class ChatBotComponent implements OnInit, AfterViewInit {
  @Input() variant: 'panel' | 'page' = 'page';
  @Output() closed = new EventEmitter<void>();

  @ViewChild('feed') feed?: ElementRef<HTMLElement>;
  @ViewChild('input') input?: ElementRef<HTMLTextAreaElement>;

  session = inject(ChatSessionService);
  private router = inject(Router);
  private employeeService = inject(EmployeeService);
  private stylingColoursService = inject(StylingColoursService);
  private translate = inject(TranslateService);

  draft = '';
  firstName = '';
  showJumpToLatest = false;
  private stickToBottom = true;

  readonly suggestions: Suggestion[] = [
    { key: 'ChatBot-Suggestion1', text: 'How much holiday do I have left?', icon: 'beach_access' },
    { key: 'ChatBot-Suggestion2', text: 'Request time off next Friday', icon: 'event_available' },
    { key: 'ChatBot-Suggestion3', text: "What's our sick leave policy?", icon: 'policy' },
    { key: 'ChatBot-Suggestion4', text: 'Update my home address', icon: 'home_work' }
  ];

  @HostBinding('class') get hostClass() {
    return `chat chat--${this.variant}`;
  }

  @HostBinding('style.--chat-accent') get accent() {
    return this.stylingColoursService.activeStylingColour?.primaryColour || DEFAULT_ACCENT;
  }

  constructor() {
    // Follow the conversation as it streams, unless the user has scrolled up to read.
    effect(() => {
      this.session.messages();
      queueMicrotask(() => (this.stickToBottom ? this.scrollToBottom() : (this.showJumpToLatest = true)));
    });
  }

  ngOnInit() {
    const user = this.employeeService.getFetchedCurrentUserDetails();
    this.firstName = user?.preferredName || user?.firstname || '';
    this.session.ensureConnected();
  }

  ngAfterViewInit() {
    this.scrollToBottom();
    if (this.variant === 'panel') setTimeout(() => this.input?.nativeElement.focus());
  }

  send(text = this.draft) {
    if (!text.trim() || this.session.sending()) return;
    this.draft = '';
    this.stickToBottom = true;
    this.session.send(text);
    this.input?.nativeElement.focus();
  }

  sendSuggestion(s: Suggestion) {
    this.send(this.translate.instant(s.key, { Default: s.text }));
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.send();
    }
  }

  newChat() {
    this.session.newConversation();
    this.draft = '';
    this.input?.nativeElement.focus();
  }

  openFullPage() {
    this.closed.emit();
    this.router.navigateByUrl(CHAT_BOT_ROUTE);
  }

  retryConnection() {
    this.session.ensureConnected();
  }

  onScroll() {
    const el = this.feed?.nativeElement;
    if (!el) return;
    this.stickToBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    if (this.stickToBottom) this.showJumpToLatest = false;
  }

  scrollToBottom(smooth = false) {
    const el = this.feed?.nativeElement;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    this.stickToBottom = true;
    this.showJumpToLatest = false;
  }

  trackMessage(_: number, message: ChatMessage) {
    return message.id;
  }
}
